import { supabase } from '@/integrations/supabase/client';
import { SearchFilters, SearchResult } from './types';

export abstract class SearchBuilder {
  protected filters: SearchFilters;

  constructor(filters: SearchFilters) {
    this.filters = filters;
  }

  /**
   * Build the base query for the search
   */
  protected abstract buildBaseQuery(): ReturnType<typeof supabase.from>;

  /**
   * Apply text search filters
   */
  protected applyTextSearch(query: ReturnType<typeof supabase.from>): ReturnType<typeof supabase.from> {
    if (this.filters.query && this.filters.query.trim()) {
      const searchTerm = this.filters.query.trim();
      return query.or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`);
    }
    return query;
  }

  /**
   * Apply location-based filters
   */
  protected applyLocationFilters(query: ReturnType<typeof supabase.from>): ReturnType<typeof supabase.from> {
    if (this.filters.location && this.filters.radius) {
      const { lat, lng } = this.filters.location;
      const radiusInDegrees = this.filters.radius / 111000; // Convert meters to approximate degrees
      
      return query
        .gte('latitude', lat - radiusInDegrees)
        .lte('latitude', lat + radiusInDegrees)
        .gte('longitude', lng - radiusInDegrees)
        .lte('longitude', lng + radiusInDegrees);
    }
    return query;
  }

  /**
   * Apply rating filters
   */
  protected applyRatingFilters(query: ReturnType<typeof supabase.from>): ReturnType<typeof supabase.from> {
    if (this.filters.minRating !== undefined) {
      query = query.gte('average_rating', this.filters.minRating);
    }
    if (this.filters.maxRating !== undefined) {
      query = query.lte('average_rating', this.filters.maxRating);
    }
    return query;
  }

  /**
   * Apply holiday type filters
   */
  protected applyHolidayFilters(query: ReturnType<typeof supabase.from>): ReturnType<typeof supabase.from> {
    if (this.filters.holidayTypes && this.filters.holidayTypes.length > 0) {
      const conditions = this.filters.holidayTypes.map(type => `holiday_type.eq.${type}`);
      return query.or(conditions.join(','));
    }
    return query;
  }

  /**
   * Apply sorting
   */
  protected applySorting(query: ReturnType<typeof supabase.from>): ReturnType<typeof supabase.from> {
    const { sortBy = 'relevance', sortOrder = 'desc' } = this.filters;
    
    switch (sortBy) {
      case 'rating':
        return query.order('average_rating', { ascending: sortOrder === 'asc' });
      case 'name':
        return query.order('name', { ascending: sortOrder === 'asc' });
      case 'created_at':
        return query.order('created_at', { ascending: sortOrder === 'asc' });
      case 'distance':
        // Distance sorting requires special handling with location
        if (this.filters.location) {
          return query.order('latitude', { ascending: sortOrder === 'asc' });
        }
        break;
      case 'relevance':
      default:
        // Default relevance sorting based on rating and recent activity
        return query.order('average_rating', { ascending: false })
                   .order('created_at', { ascending: false });
    }
    
    return query;
  }

  /**
   * Apply pagination
   */
  protected applyPagination(query: ReturnType<typeof supabase.from>): ReturnType<typeof supabase.from> {
    const { limit = 20, offset = 0 } = this.filters;
    return query.range(offset, offset + limit - 1);
  }

  /**
   * Calculate distance between two points
   */
  protected calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lng2 - lng1) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c;
  }

  /**
   * Calculate relevance score
   */
  protected calculateRelevanceScore(item: Record<string, unknown>): number {
    let score = 0;
    
    // Base score from rating
    const rating = item.average_rating as number || 0;
    score += rating * 20; // Max 100 points from rating
    
    // Boost recent items
    const createdAt = new Date(item.created_at as string).getTime();
    const now = Date.now();
    const daysSinceCreation = (now - createdAt) / (1000 * 60 * 60 * 24);
    const recencyBoost = Math.max(0, 30 - daysSinceCreation) * 2; // Max 60 points
    score += recencyBoost;
    
    // Text match boost
    if (this.filters.query) {
      const query = this.filters.query.toLowerCase();
      const name = (item.name as string || '').toLowerCase();
      const description = (item.description as string || '').toLowerCase();
      
      if (name.includes(query)) {
        score += 50;
      }
      if (description.includes(query)) {
        score += 25;
      }
    }
    
    return Math.round(score);
  }

  /**
   * Execute the search and return results
   */
  public abstract execute(): Promise<SearchResult[]>;
}